// @ts-nocheck
import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { getUser } from "../auth/auth";
import { performLogout } from "../auth/logout";
import "../styles/AccountMenu.css";

export default function AccountMenu({ onLaunchScanner }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.matchMedia("(max-width: 768px)").matches);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);
  const closeButtonRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = getUser();
  const tradeRouteActive =
    location.pathname === "/trades" ||
    location.pathname.startsWith("/trade-offer/") ||
    location.pathname.startsWith("/offer/");
  const postRouteActive =
    location.pathname === "/local-trades" ||
    location.pathname.startsWith("/post-trade") ||
    location.pathname.startsWith("/post-bounty") ||
    location.pathname.startsWith("/bounty-offer/");
  const profileRouteActive = location.pathname === "/profile" || location.pathname.startsWith("/settings");

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 768px)");
    const handleChange = event => setIsMobile(event.matches);

    setIsMobile(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    }

    if (isOpen && !isMobile) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, isMobile]);

  useEffect(() => {
    if (!isOpen || !isMobile) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, isMobile]);

  useEffect(() => {
    if (!isOpen) {
      triggerRef.current?.focus();
    }
  }, [isOpen]);

  const handleLogout = () => {
    setIsOpen(false);
    performLogout();
  };

  const closeMenu = () => setIsOpen(false);
  const closeThen = (callback) => {
    setIsOpen(false);
    window.setTimeout(() => {
      callback?.();
    }, 0);
  };
  const handleMobileNavigate = (path) => {
    closeThen(() => navigate(path));
  };
  const handleMobileScannerLaunch = (mode) => {
    closeThen(() => onLaunchScanner?.(mode));
  };

  const mobileNavSections = (
    <>
      <div className="account-menu-section account-menu-mobile-nav-section">
        <div className="account-menu-section-title">Pages</div>
        <button
          type="button"
          className={`account-menu-item ${location.pathname === "/home" ? "active" : ""}`}
          onClick={() => handleMobileNavigate("/home")}
        >
          <span className="menu-row-left">
            <span className="menu-label">Bounties</span>
          </span>
          <span className="menu-chevron">&gt;</span>
        </button>
        <button
          type="button"
          className={`account-menu-item ${tradeRouteActive ? "active" : ""}`}
          onClick={() => handleMobileNavigate("/trades")}
        >
          <span className="menu-row-left">
            <span className="menu-label">Trades</span>
          </span>
          <span className="menu-chevron">&gt;</span>
        </button>
        <button
          type="button"
          className={`account-menu-item ${location.pathname === "/messages" ? "active" : ""}`}
          onClick={() => handleMobileNavigate("/messages")}
        >
          <span className="menu-row-left">
            <span className="menu-label">Messages</span>
          </span>
          <span className="menu-chevron">&gt;</span>
        </button>
        <button
          type="button"
          className={`account-menu-item ${location.pathname === "/events" ? "active" : ""}`}
          onClick={() => handleMobileNavigate("/events")}
        >
          <span className="menu-row-left">
            <span className="menu-label">Events</span>
          </span>
          <span className="menu-chevron">&gt;</span>
        </button>
        <button
          type="button"
          className={`account-menu-item ${profileRouteActive ? "active" : ""}`}
          onClick={() => handleMobileNavigate("/profile")}
        >
          <span className="menu-row-left">
            <span className="menu-label">Profile</span>
          </span>
          <span className="menu-chevron">&gt;</span>
        </button>
      </div>

      <div className="account-menu-section account-menu-mobile-nav-section">
        <div className="account-menu-section-title">Actions</div>
        <button
          type="button"
          className={`account-menu-item ${postRouteActive ? "active" : ""}`}
          onClick={() => handleMobileNavigate("/local-trades")}
        >
          <span className="menu-row-left">
            <span className="menu-label">Post</span>
          </span>
          <span className="menu-chevron">&gt;</span>
        </button>
        <div className="account-menu-action-group">
          <div className="account-menu-action-label">Scanner</div>
          <div className="account-menu-action-grid">
            <button
              type="button"
              className="account-menu-item account-menu-item-action"
              onClick={() => handleMobileScannerLaunch("camera")}
            >
              <span className="menu-row-left">
                <span className="menu-label">Scan Card</span>
              </span>
            </button>
            <button
              type="button"
              className="account-menu-item account-menu-item-action"
              onClick={() => handleMobileScannerLaunch("upload")}
            >
              <span className="menu-row-left">
                <span className="menu-label">Upload Card</span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </>
  );

  const menuSections = (
    <>
      <div className="account-menu-section">
        <div className="account-menu-section-title">Account</div>
        <Link to="/profile?tab=plan" className="account-menu-item" tabIndex={0} onClick={closeMenu}>
          <span className="menu-row-left">
            <span className="menu-label">Plan & Usage</span>
          </span>
          <span className="menu-chevron">&gt;</span>
        </Link>
        <Link to="/collection" className="account-menu-item" tabIndex={0} onClick={closeMenu}>
          <span className="menu-row-left">
            <span className="menu-label">My Collection</span>
          </span>
          <span className="menu-chevron">&gt;</span>
        </Link>
        <Link to="/settings" className="account-menu-item" tabIndex={0} onClick={closeMenu}>
          <span className="menu-row-left">
            <span className="menu-label">Account & Privacy (Settings)</span>
          </span>
          <span className="menu-chevron">&gt;</span>
        </Link>
        <button className="account-menu-item" onClick={handleLogout} tabIndex={0}>
          <span className="menu-row-left">
            <span className="menu-label">Log Out</span>
          </span>
          <span className="menu-chevron">&gt;</span>
        </button>
      </div>
      <div className="account-menu-section">
        <div className="account-menu-section-title">Safety</div>
        <Link to="/what-is-cardex" className="account-menu-item" tabIndex={0} onClick={closeMenu}>
          <span className="menu-row-left">
            <span className="menu-label">What is Cardex?</span>
          </span>
          <span className="menu-chevron">&gt;</span>
        </Link>
        <Link to="/community-guidelines" className="account-menu-item" tabIndex={0} onClick={closeMenu}>
          <span className="menu-row-left">
            <span className="menu-label">Community Guidelines</span>
          </span>
          <span className="menu-chevron">&gt;</span>
        </Link>
        <Link to="/report" className="account-menu-item" tabIndex={0} onClick={closeMenu}>
          <span className="menu-row-left">
            <span className="menu-label">Report Abuse</span>
          </span>
          <span className="menu-chevron">&gt;</span>
        </Link>
      </div>
      <div className="account-menu-section">
        <div className="account-menu-section-title">Legal</div>
        <Link to="/terms" className="account-menu-item" tabIndex={0} onClick={closeMenu}>
          <span className="menu-row-left">
            <span className="menu-label">Terms of Service</span>
          </span>
          <span className="menu-chevron">&gt;</span>
        </Link>
        <Link to="/privacy" className="account-menu-item" tabIndex={0} onClick={closeMenu}>
          <span className="menu-row-left">
            <span className="menu-label">Privacy Policy</span>
          </span>
          <span className="menu-chevron">&gt;</span>
        </Link>
        <Link to="/ip-disclaimer" className="account-menu-item" tabIndex={0} onClick={closeMenu}>
          <span className="menu-row-left">
            <span className="menu-label">IP & Trademark</span>
          </span>
          <span className="menu-chevron">&gt;</span>
        </Link>
      </div>
      {currentUser?.role === "admin" && (
        <div className="account-menu-section">
          <div className="account-menu-section-title">Admin</div>
          <Link to="/admin-reports" className="account-menu-item" tabIndex={0} onClick={closeMenu}>
            <span className="menu-row-left">
              <span className="menu-label">Reports Queue</span>
            </span>
            <span className="menu-chevron">&gt;</span>
          </Link>
          <Link to="/admin-disputes" className="account-menu-item" tabIndex={0} onClick={closeMenu}>
            <span className="menu-row-left">
              <span className="menu-label">Disputes</span>
            </span>
            <span className="menu-chevron">&gt;</span>
          </Link>
          <Link to="/admin-verifications" className="account-menu-item" tabIndex={0} onClick={closeMenu}>
            <span className="menu-row-left">
              <span className="menu-label">Verifications</span>
            </span>
            <span className="menu-chevron">&gt;</span>
          </Link>
          <Link to="/admin-logs" className="account-menu-item" tabIndex={0} onClick={closeMenu}>
            <span className="menu-row-left">
              <span className="menu-label">Moderation Logs</span>
            </span>
            <span className="menu-chevron">&gt;</span>
          </Link>
          <Link to="/admin-waitlist" className="account-menu-item" tabIndex={0} onClick={closeMenu}>
            <span className="menu-row-left">
              <span className="menu-label">Waitlist</span>
            </span>
            <span className="menu-chevron">&gt;</span>
          </Link>
          <Link to="/admin-blogs" className="account-menu-item" tabIndex={0} onClick={closeMenu}>
            <span className="menu-row-left">
              <span className="menu-label">Blogs</span>
            </span>
            <span className="menu-chevron">&gt;</span>
          </Link>
        </div>
      )}
      <div className="account-menu-section account-menu-danger">
        <div className="account-menu-section-title">Danger Zone</div>
        <Link to="/account-deletion" className="account-menu-item account-menu-item-danger" tabIndex={0} onClick={closeMenu}>
          <span className="menu-row-left">
            <span className="menu-label">Request Account Deletion</span>
          </span>
          <span className="menu-chevron">&gt;</span>
        </Link>
      </div>
    </>
  );

  return (
    <div className="account-menu" style={{ position: "relative" }} ref={menuRef}>
      <button
        ref={triggerRef}
        className="account-menu-trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Account & Safety Menu"
        aria-expanded={isOpen}
        aria-controls="account-menu-content"
      >
        <span className="hamburger-icon">☰</span>
      </button>

      {isOpen && !isMobile && (
          <div
            id="account-menu-content"
            className="account-menu-dropdown"
            onClick={e => e.stopPropagation()}
            onMouseDown={e => e.stopPropagation()}
            tabIndex={-1}
          >
            <div className="account-menu-header">Account & Safety</div>
            {menuSections}
          </div>
      )}

      {isOpen && isMobile
        ? createPortal(
            <div className="account-menu-mobile-root">
              <div className="account-menu-mobile-backdrop" onClick={closeMenu} />
              <div className="account-menu-mobile-panel" role="dialog" aria-modal="true" aria-labelledby="account-menu-mobile-title">
                <div className="account-menu-mobile-header">
                  <h2 id="account-menu-mobile-title">Menu</h2>
                  <button
                    ref={closeButtonRef}
                    className="account-menu-mobile-close"
                    onClick={closeMenu}
                    aria-label="Close account menu"
                  >
                    ✕
                  </button>
                </div>
                <div className="account-menu-mobile-content" id="account-menu-content">
                  {mobileNavSections}
                  {menuSections}
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
